import React, { useState } from 'react';
import {
  Box, Card, CardContent, CardHeader, Divider, Stack, TextField, MenuItem,
  Button, InputAdornment, IconButton, RadioGroup, FormControlLabel, Radio,
  FormLabel, FormControl, Snackbar, Alert, CircularProgress, Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { getBearerToken, postBearerToken, putBearerToken } from '../../utils/Apis';
import { useLocation, useNavigate } from 'react-router-dom';
import AppShell from '../../layouts/AppShell';
import './cssClasses/custom.css';

const A_add_room = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = location.pathname === '/edit_room';

  const [name, set_name] = React.useState('');
  const [password, set_password] = React.useState('');
  const [is_active, setis_active] = useState('');
  const [host_of_room, sethost_of_room] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [message, setmessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [messageView, setmessageView] = useState(false);
  const [loader, setloader] = useState(false);
  const [fetchLoader, setfetchLoader] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [user_data, setuser_data] = useState([]);

  React.useEffect(() => {
    fetch_users();
    if (location?.state?.id) fetch_details();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notify = (msg, sev = 'info') => { setmessage(msg); setSeverity(sev); setmessageView(true); };

  const fetch_details = async () => {
    getBearerToken('room?_id=' + location.state.id).then((result) => {
      if (result.error === 0) {
        set_name(result.data.name);
        set_password(result.data.password);
        sethost_of_room(result.data.host_of_room);
        setis_active(result.data.is_active === true ? 'Active' : 'Inactive');
      } else {
        setfetchLoader(false);
      }
    });
  };

  const fetch_users = async () => {
    setfetchLoader(true);
    getBearerToken('users?for_admin=1&type=SubAdmin').then((result) => {
      if (result.error === 0) setuser_data(result.data);
      setfetchLoader(false);
    });
  };

  // Validation mirrors the original rules (create: name/password/host; edit: name).
  const nameError = submitted && name === '';
  const passwordError = submitted && !isEdit && password === '';
  const hostError = submitted && !isEdit && host_of_room === '';

  const submit = async () => {
    setSubmitted(true);
    let url = 'room';
    let json;

    if (!isEdit) {
      json = { name, host_of_room, password, is_active: is_active === 'Active' };
    } else {
      json = password !== ''
        ? { name, host_of_room, password, is_active: is_active === 'Active' }
        : { name, host_of_room, is_active: is_active === 'Active' };
      json['_id'] = location.state.id;
    }

    if (!isEdit) {
      if (name === '' || password === '' || host_of_room === '') {
        notify('All fields are compulsory', 'error');
        return;
      }
      setloader(true);
      postBearerToken(url, json).then((result) => {
        setloader(false);
        if (result.error === 0) {
          set_name(''); sethost_of_room(''); set_password(''); setis_active(''); setSubmitted(false);
          notify('Room created successfully', 'success');
        } else {
          notify(result.message || 'Something went wrong', 'error');
        }
      });
    } else {
      if (name === '') { notify('Name is compulsory', 'error'); return; }
      setloader(true);
      putBearerToken(url, json).then((result) => {
        setloader(false);
        if (result.error === 0) notify('Room updated successfully', 'success');
        else notify(result.message || 'Something went wrong', 'error');
      });
    }
  };

  return (
    <AppShell title={isEdit ? 'Edit Room' : 'Add Room'} breadcrumb={['Rooms', isEdit ? 'Edit' : 'Add']}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} color="inherit" onClick={() => navigate('/room_list')} sx={{ color: 'text.secondary' }}>
          Back to rooms
        </Button>
      </Stack>

      <Box sx={{ maxWidth: 720 }}>
        <Card>
          <CardHeader
            title={isEdit ? 'Edit room details' : 'Create a new room'}
            subheader="Assign a host, set a name and password, and control availability."
            titleTypographyProps={{ variant: 'h5' }}
          />
          <Divider />
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            {fetchLoader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <Stack spacing={3}>
                <TextField
                  select label="Host" value={host_of_room}
                  onChange={(e) => sethost_of_room(e.target.value)}
                  error={hostError} helperText={hostError ? 'Please select a host' : 'The host who will run this room'}
                >
                  <MenuItem value=""><em>Select host</em></MenuItem>
                  {user_data && user_data.map((item) => (
                    <MenuItem key={item._id} value={item._id}>{item.slug_name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Room name" value={name} onChange={(e) => set_name(e.target.value)}
                  inputProps={{ autoComplete: 'off', name: 'new-room-name' }}
                  error={nameError} helperText={nameError ? 'Room name is required' : ' '}
                />

                <TextField
                  label="Password" type={showPassword ? 'text' : 'password'}
                  inputProps={{ autoComplete: 'new-password', name: 'new-room-password' }}
                  value={password} onChange={(e) => set_password(e.target.value)}
                  error={passwordError}
                  helperText={passwordError ? 'Password is required' : (isEdit ? 'Leave blank to keep the current password' : ' ')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" aria-label="toggle password visibility">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl>
                  <FormLabel sx={{ mb: 0.5, fontWeight: 600 }}>Status</FormLabel>
                  <RadioGroup row value={is_active} onChange={(e) => setis_active(e.target.value)}>
                    <FormControlLabel value="Active" control={<Radio />} label="Active" />
                    <FormControlLabel value="Inactive" control={<Radio />} label="Inactive" />
                  </RadioGroup>
                </FormControl>

                <Divider />
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button color="inherit" onClick={() => navigate('/room_list')} sx={{ color: 'text.secondary' }}>Cancel</Button>
                  <Button
                    variant="contained" onClick={submit} disabled={loader}
                    startIcon={loader ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                  >
                    {loader ? 'Saving…' : (isEdit ? 'Save changes' : 'Create room')}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Fields marked required must be completed before saving.
        </Typography>
      </Box>

      <Snackbar
        open={messageView} autoHideDuration={4000} onClose={() => setmessageView(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={severity} variant="filled" onClose={() => setmessageView(false)} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </AppShell>
  );
};

export default A_add_room;
